import React, { useEffect, useRef, useState } from "react";
import Button from "../../components/UI/Buttons/Button/Button";
import {
  LogoSlot,
  validateLogoFile,
} from "../../services/associationMediaPayload";
import { mediaUrl } from "../AdvisorProfilePage/MediaManager";
import styles from "./LogoPicker.module.scss";

// The association's logo slot inside the media card (apoteka-back @ cbd77705):
// one image, kept apart from the banners. Picking a file replaces whatever the
// slot holds and "Ukloni logo" empties it; nothing reaches the server until
// the card saves. With no logo the slot shows the name's initials, which is
// what the apps show on the association card in that case.

/**
 * "UD" for "Udruženje dijabetičara Srbije": the first letters of the first two
 * words, split on whitespace and hyphens like the iOS card does.
 */
export const associationInitials = (name: string): string =>
  name
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");

interface LogoPickerProps {
  /** The association's name, for the initials shown while there is no logo. */
  name: string;
  logo: LogoSlot;
  disabled: boolean;
  onChange: (next: LogoSlot) => void;
  error: string | null;
  onError: (message: string | null) => void;
}

const LogoPicker: React.FC<LogoPickerProps> = ({
  name,
  logo,
  disabled,
  onChange,
  error,
  onError,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingFile = logo?.kind === "pending" ? logo.file : null;
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);

  // A picked file previews from a blob URL, released once the file is
  // replaced, removed or saved.
  useEffect(() => {
    if (!pendingFile) {
      setPendingUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingFile);
    setPendingUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingFile]);

  const pick = (files: FileList | null) => {
    const file = files?.[0];
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    if (!file) {
      return;
    }
    const problem = validateLogoFile(file);
    if (problem) {
      onError(problem);
      return;
    }
    onError(null);
    onChange({ kind: "pending", file });
  };

  const remove = () => {
    onError(null);
    onChange(null);
  };

  const src =
    logo?.kind === "stored" ? mediaUrl(logo.file.location) : pendingUrl;

  let preview: React.ReactNode;
  if (src) {
    preview = <img className={styles.preview} src={src} alt="Logo udruženja" />;
  } else if (pendingFile) {
    // The blob URL arrives a frame after the pick; keep the tile empty.
    preview = <span className={styles.preview} aria-hidden="true" />;
  } else {
    preview = (
      <span
        className={styles.initials}
        role="img"
        aria-label="Logo nije postavljen"
      >
        {associationInitials(name)}
      </span>
    );
  }

  return (
    <div className={styles.logoPicker} data-testid="logo-picker">
      <h3 className={styles.subTitle}>Logo</h3>
      <p className={styles.hint}>
        Prikazuje se na kartici udruženja. Slika do 10 MB (JPEG, PNG, WebP).
      </p>
      <div className={styles.row}>
        {preview}
        <div className={styles.actions}>
          {pendingFile && (
            <span className={styles.pending}>
              Novi logo: {pendingFile.name}
            </span>
          )}
          {logo && (
            <Button buttonType="white" disabled={disabled} onClick={remove}>
              Ukloni logo
            </Button>
          )}
        </div>
      </div>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <input
        ref={inputRef}
        className={styles.fileInput}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        disabled={disabled}
        aria-label={logo ? "Promenite logo" : "Dodajte logo"}
        onChange={(e) => pick(e.target.files)}
      />
    </div>
  );
};

export default LogoPicker;
